<template>
    <BasePanel
        headline="lang_LABEL_RELEASES_PANEL_CAPTION_COSTS"
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
                <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                :no-results-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
                :items-per-page="100"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row class="pb-3 pl-4">
                        <v-col cols="2">
                            <DropdownFilter 
                                context="ReleasesFilterCostGroup"
                                label="lang_LABEL_RELEASES_CAPTION_COST_GROUP"
                                :items="costGroupItems"
                                :alvqa="alvqa + '-dropdown-filter-cost-group'"
                                :defaultValueNull="true"
                            />
                        </v-col>
                        <v-col 
                            cols="2"
                            class="pt-8"
                            :alvqa="alvqa + '-sum'"
                        >
                            {{ $t('lang_GENERIC_CAPTION_SUM') }}:
                            {{ $n(getSumOfDisplayedRows, 'number') }} {{ $store.getters.getSelectedCurrency }}
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col 
                            cols="2"
                            align-self="end"
                        >
                            <BaseButton 
                                label="lang_GENERIC_CAPTION_EXPORT"
                                @click="exportAllCosts"
                                block
                                icon="fa-file-excel"
                                :alvqa="alvqa + '-list-button-export-all'"
                            />
                        </v-col>
                        <v-col 
                            cols="2"
                            align-self="end"
                        >
                            <BaseButton 
                                @click="openCostsplittingDialog"
                                label="lang_LABEL_RELEASES_COST_SPLITTING"
                                block
                                :alvqa="alvqa + '-button-costsplitting'"
                            />
                        </v-col>
                        <v-col 
                            cols="3"
                            align-self="end"
                        >
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- Items -->
                <template v-slot:[`item.costGroup`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-cost-group'">
                        {{ getCostGroup(item.costGroup) }}
                    </div>
                </template>
                <template v-slot:[`item.date`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-date'">
                        {{ item.date !== null ? $d(new Date(item.date), 'alvDateFormat') : '' }}
                    </div>
                </template>
                <template v-slot:[`item.recipient`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="getRecipientInfo(item.recipient)"
                            :alvqa="alvqa + '-list-row-text-recipient'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.documentNumber`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-document-number'">
                        {{ item.documentNumber }}
                    </div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-amount'"
                    >
                        {{ $n(item.amount, 'number') }}
                    </div>
                </template>
                <template v-slot:[`item.description`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.description"
                            :alvqa="alvqa + '-list-row-text-description'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.country`]="{ item }">
                    <div>
                        <BaseTooltip>
                            <template v-slot:content>
                                <div 
                                    :alvqa="alvqa + '-list-row-text-country'"
                                >{{ item.country }}</div>
                            </template>
                            <template v-slot:text>{{ $alvPlugin.getTranslatedCountryName(item.country, true) }}</template>
                        </BaseTooltip>
                    </div>
                </template>
                <template v-slot:[`item.paidOn`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-paid-on'"
                    >
                        {{ item.paidOn !== null ? $d(new Date(item.paidOn), 'alvDateFormat') : '' }}
                    </div>
                </template>
                <template v-slot:[`item.billable`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-billable'">
                        {{ item.billable === 'no' ? $t('lang_GENERAL_NO') : item.billable }}
                    </div>
                </template>

            <!-- Buttons -->
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        v-if="item.billable === 'no'"
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-icon-delete'"
                    />
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>

<script>
import { DropdownGenericCostGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseTooltip from '@/components/_base/BaseTooltip'
import DropdownFilter from '@/components/_filters/DropdownFilter'

export default {
    name: "ReleasesCostsEditPanel",
    components: {
        BasePanel,
        BaseButton,
        BaseIcon,
        BaseTooltip,
        DropdownFilter
    },
    data() {
        return {
            alvqa: 'label-releases-edit-form-panel-costs'
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_COST_GROUP"), 
                    align: "left", 
                    value: "costGroup",
                    class: 'text-no-wrap',
                    width: '150px',
                    alvqa: this.alvqa + '-header-cost-group',
                    sortable: false,
                    filter: this.filterCostGroup,
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_DATE"), 
                    align: "left", 
                    value: "date",
                    width: "110px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-date'
                },                
                {
                    text: this.$t("lang_GENERIC_CAPTION_RECIPIENT"), 
                    align: "left", 
                    value: "recipient",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-recipient'
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_DESCRIPTION"), 
                    align: "left", 
                    value: "description",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-description'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_AMOUNT") + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "amount",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-amount'
                },

                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_DOCUMENT_NUMBER"), 
                    align: "left", 
                    value: "documentNumber",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-document-number'
                },
                {
                    text: this.$t('lang_GENERAL_CAPTION_COUNTRY'), 
                    value: 'country',
                    align: 'left', 
                    width: '90px',
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-country'
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_PAID_ON"), 
                    align: "right", 
                    value: "paidOn",
                    width: "110px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-paid-on'
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_BILLABLE"), 
                    align: "left", 
                    value: "billable",
                    class: 'text-no-wrap',
                    width: '120px',
                    sortable: false,
                    alvqa: this.alvqa + '-header-billable'
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },

        listItems() { return this.$store.getters.getReleasesCosts },
        costGroupItems() {
            let costGroup = JSON.parse(JSON.stringify(DropdownGenericCostGroupTypes))
            costGroup.forEach(element => {
                element.text = this.$t(element.text)
            });
            costGroup.sort((a,b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            return costGroup
        },
        getSumOfDisplayedRows() {
            let costs = this.$store.getters.getReleasesCosts
            let selected = this.$store.getters.getSelectedReleasesFilterCostGroup
            let sum = 0

            // if no filter is set, just sum up the amount of all rows.
            if(!selected) sum = costs.reduce((prev, cur) => prev + this.$alvPlugin.stringToNumber(cur.amount), 0)
            else { // if the filter is set
                costs.forEach(row => {
                    if(row.costGroup === selected) sum += this.$alvPlugin.stringToNumber(row.amount)
                })
            }
            
            return sum
        }
    },
    methods: {
        getCostGroup(costGroupvalue) {
            let costGroupsTypes = DropdownGenericCostGroupTypes;

            if (costGroupsTypes !== undefined) {
                let match = costGroupsTypes.find(type => {
                    return type.value === costGroupvalue;
                });

                // Skip if `match` is undefined
                if (match) {
                    return this.$t(match.text);
                } else {
                    console.warn(`Cost group value "${costGroupvalue}" not found in DropdownGenericCostGroupTypes.`);
                    return ''; // Skip by returning an empty string
                }
            } else {
                console.error('DropdownGenericCostGroupTypes is undefined.');
                return '';
            }
        },
        getRecipientInfo(item){
            if(item === null){
                return
            }else{
                let name = this.$alvPlugin.printName(item) ? '(' + item.firstName + ' ' + item.lastName +')' : ''
                
                if(item.inheritFromGroup === true && item.inheritFromGroupObject !== null){
                    let group = item.inheritFromGroupObject.inv_company_name

                    return group + ' ' + name
                }else{
                    if(item.group !== null && item.group !== '' &&  item.group !== undefined){
                        return item.group + ' ' + name
                    }else{
                        return item.gemaName + ' ' + name
                    }
                }
            }
        },
        filterCostGroup(value) {
            // https://www.codeply.com/p/krbUh6q9yE
            let selected = this.$store.getters.getSelectedReleasesFilterCostGroup

            if (!selected) return true // return "true" for all rows in data table = show all rows (no filter selected)
            else return value === selected // check if the value of the row is matching the filter and show row in data table
        },
        exportAllCosts() {
            this.$store.dispatch('exportAllReleasesCosts')
        },
        deleteRow(item) {
            // set the selected row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedReleasesCosts", item)
            this.$store.commit('setDeletionDialogContext', 'SelectedReleasesCosts')
            this.$store.commit('setShowDeletionDialog', true)
        },
        addNewRow() {
            this.$store.commit('setShowReleasesCostsEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultReleasesCosts)) 
            this.$store.commit('setReleasesCostsEditingDialogDetails', defaultValues)
        },
        editInlineFunction(item) {
            this.$store.commit('setSelectedReleasesCosts', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            detailsDeepCopy['amount'] = this.$n(detailsDeepCopy['amount'], 'number')

            // set the copy in dialog details to edit
            this.$store.commit('setReleasesCostsEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setReleasesCostsRecipientOptions', [item.recipient])
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowReleasesCostsEditingDialog', true) 
        },
        openCostsplittingDialog() {
            let defaults = JSON.parse(JSON.stringify(this.$store.getters.getDefaultReleaseCostsplittingDialogDetails))
            this.$store.commit('setReleaseCostsplittingDialogDetails', defaults)
            this.$store.commit('setShowReleaseCostsplittingDialog', true)
        }
    },
    mounted() {
        this.$store.commit('setCountries')
    }
}
</script>